import { useParams, Link } from 'react-router-dom';
import { manhuaDetailMap, manhuaFAQ } from '@/mocks/manhuaData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useEffect } from 'react';

export default function ManhuaDetailPage() {
  const { id } = useParams<{ id: string }>();

  const manhua = id ? manhuaDetailMap[id] : undefined;

  useEffect(() => {
    if (!manhua) return;
    document.title = `${manhua.title} - 虚空韩漫网_韩漫在线阅读`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `${manhua.title}免费在线阅读。${manhua.description.substring(0, 120)}。虚空韩漫网提供无删减下拉式韩漫阅读体验。`);
    const keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) keywordsMeta.setAttribute('content', `${manhua.title},韩国漫画,韩漫在线阅读,虚空韩漫网`);
  }, [manhua]);

  useEffect(() => {
    if (!manhua || !id) return;

    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ComicStory',
      name: manhua.title,
      author: { '@type': 'Person', name: manhua.author },
      description: manhua.description,
      genre: manhua.genre.split(' / '),
      inLanguage: 'zh-CN',
      datePublished: '2024-01-15',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: manhua.rating,
        bestRating: '10',
        ratingCount: parseInt(manhua.views.replace(/[^0-9]/g, '')),
      },
    });
    document.head.appendChild(articleScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://nh5688.com/' },
        { '@type': 'ListItem', position: 2, name: manhua.genre.split(' / ')[0], item: `http://nh5688.com/category/all` },
        { '@type': 'ListItem', position: 3, name: manhua.title, item: `http://nh5688.com/manhua/${id}` },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(articleScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, [manhua, id]);

  if (!manhua) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-2xl font-heading text-foreground-900 mb-2">作品未找到</h1>
          <p className="text-foreground-500 mb-6">该漫画不存在或已被移除</p>
          <Link to="/" className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <div className="px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: manhua.genre.split(' / ')[0], path: '/category/all' },
              { label: manhua.title },
            ]}
          />

          <article className="mt-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-72 shrink-0">
                <div className="sticky top-24">
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={manhua.coverImageUrl}
                      alt={manhua.title}
                      title={`${manhua.title} - 虚空韩漫网韩漫在线阅读`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {manhua.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-2">
                  {manhua.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {manhua.author}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-price-tag-3-line"></i>
                    {manhua.genre}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1 text-accent-600 font-medium">
                    <i className="ri-star-fill"></i>
                    {manhua.rating}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {manhua.views}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1 text-primary-600">
                    <i className="ri-bookmark-line"></i>
                    {manhua.favorites} 收藏
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    manhua.status === '连载中' ? 'bg-green-100 text-green-700' : 'bg-foreground-100 text-foreground-600'
                  }`}>
                    {manhua.status}
                  </span>
                  <span className="text-foreground-500 text-xs">
                    共 {manhua.totalChapters} 话
                  </span>
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-sm font-medium text-foreground-800 mb-3">作品简介</h2>
                  <div className="text-foreground-600 text-sm leading-relaxed">
                    <p>{manhua.description}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <Link
                    to={`/manhua/${id}/chapter/1`}
                    className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer text-center"
                  >
                    <i className="ri-play-circle-line mr-1.5"></i>
                    开始阅读 第1话
                  </Link>
                  <Link
                    to={`/manhua/${id}/chapter/${manhua.totalChapters}`}
                    className="px-6 py-3 rounded-lg bg-secondary-100 text-secondary-800 text-sm font-medium whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer text-center"
                  >
                    <i className="ri-skip-forward-line mr-1.5"></i>
                    最新章节 第{manhua.totalChapters}话
                  </Link>
                  <button className="px-6 py-3 rounded-lg border border-background-200 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line mr-1.5"></i>
                    加入收藏
                  </button>
                </div>

                <div>
                  <h2 className="text-lg font-heading text-foreground-950 mb-4">
                    章节列表
                    <span className="text-foreground-500 text-sm font-normal ml-2 font-sans">
                      共 {manhua.totalChapters} 话
                    </span>
                  </h2>
                  <div className="bg-background-100 rounded-xl overflow-hidden">
                    <div className="max-h-[500px] overflow-y-auto">
                      {manhua.chapters.map((chapter) => (
                        <Link
                          key={chapter.number}
                          to={`/manhua/${id}/chapter/${chapter.number}`}
                          className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/70 last:border-0 hover:bg-background-50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-foreground-900 text-sm font-medium whitespace-nowrap">
                              第{chapter.number}话
                            </span>
                            <span className="text-foreground-500 text-sm truncate">
                              {chapter.title}
                            </span>
                          </div>
                          <span className="text-foreground-400 text-xs whitespace-nowrap ml-4">
                            {chapter.updatedAt}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {manhua.relatedManhua.length > 0 && (
            <section className="mt-16" aria-labelledby="related-title">
              <h2 id="related-title" className="font-heading text-xl text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {manhua.relatedManhua.map((item) => (
                  <Link
                    key={item.id}
                    to={`/manhua/${item.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        title={`${item.title} - 虚空韩漫网`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-foreground-900 text-sm font-medium truncate group-hover:text-primary-500 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-xs mt-1">{item.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="mt-16" aria-labelledby="faq-title">
            <h2 id="faq-title" className="font-heading text-xl text-foreground-950 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {manhuaFAQ.map((item, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                    <span className="text-foreground-800 text-sm font-medium pr-4">
                      {item.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}